import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { SelectOption } from '../../model/selectOption'
import { WbsItemType } from '../../../domain/entity/WbsItemEntity'
import { ReferencedEntityWithIcon } from '../../../domain/value-object/ReferencedEntity'
import { useWbsItemRepository } from '../../../services/adaptors/wbsItemRepositoryAdaptor'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { intl } from '../../../i18n'

export type UseProcessScopeOptionsOptions = {
  errorMessageId?: string
  enabled?: boolean
}

export type ProcessScopeOptions = {
  options: SelectOption[]
  parentMap: Record<string, string>
  isLoading: boolean
}

const CACHE_TTL_MS = 60_000

type CacheEntry = {
  promise: Promise<ReferencedEntityWithIcon[]>
  expiresAt: number
}

// 工程スコープの選択候補は、検索条件ラベル・マイルストーン対象欄など複数の
// コンポーネントから短時間に同じ内容で要求される。in-flight Promise ごと共有して
// WBS全件取得APIの重複呼び出しをまとめる。失敗したエントリは破棄して再試行を許す。
const cache = new Map<string, CacheEntry>()

// 1回の取得の失敗に対して各利用側がSnackbarを出すと同じ通知が並ぶため、取得単位で1回に絞る。
// 共有している Promise を鍵にすることで、再試行して再び失敗したときは改めて通知できる。
const notifiedFailures = new WeakSet<Promise<ReferencedEntityWithIcon[]>>()

const fetchWithCache = (
  cacheKey: string,
  fetcher: () => Promise<ReferencedEntityWithIcon[]>
): Promise<ReferencedEntityWithIcon[]> => {
  const now = Date.now()
  const cached = cache.get(cacheKey)
  if (cached && cached.expiresAt > now) {
    return cached.promise
  }
  const promise = fetcher().catch(error => {
    if (cache.get(cacheKey)?.promise === promise) {
      cache.delete(cacheKey)
    }
    throw error
  })
  cache.set(cacheKey, { promise, expiresAt: now + CACHE_TTL_MS })
  return promise
}

const toSelectOption = (entity: ReferencedEntityWithIcon): SelectOption => ({
  value: entity.uuid,
  name: entity.name,
  iconUrl: entity.iconUrl,
  tooltip: entity.path,
})

const toParentMap = (
  entities: ReferencedEntityWithIcon[]
): Record<string, string> =>
  Object.fromEntries(
    entities
      .filter(entity => entity.parentUuid)
      .map(entity => [entity.uuid, entity.parentUuid!])
  )

/**
 * ProcessScopeSearchSelectPopper に渡す選択候補(ツリー構築用の親子関係を含む)を取得する。
 *
 * @param types モジュール定数など参照が安定した配列を渡すこと(毎回新しい配列を渡すと再取得が走る)
 * @param options.errorMessageId 取得失敗時にSnackbarで通知するメッセージID。省略時は通知しない
 * @param options.enabled false の間は取得しない(閉じているダイアログでの先読みを避ける)
 */
export const useProcessScopeOptions = (
  projectUuid: string | undefined,
  types: WbsItemType[],
  { errorMessageId, enabled = true }: UseProcessScopeOptionsOptions = {}
): ProcessScopeOptions => {
  const dispatch = useDispatch()
  const { fetchListAsReferencedEntity } = useWbsItemRepository()
  const [options, setOptions] = useState<SelectOption[]>([])
  const [parentMap, setParentMap] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!projectUuid || !enabled) {
      setIsLoading(false)
      return
    }
    const cacheKey = `${projectUuid}:${types.join(',')}`
    let cancelled = false
    setIsLoading(true)
    const request = fetchWithCache(cacheKey, () =>
      fetchListAsReferencedEntity(projectUuid, types)
    )
    request
      .then(entities => {
        if (cancelled) return
        setOptions(entities.map(toSelectOption))
        setParentMap(toParentMap(entities))
      })
      .catch(error => {
        console.error('Failed to fetch process scope options:', error)
        if (cancelled) return
        if (!errorMessageId || notifiedFailures.has(request)) return
        notifiedFailures.add(request)
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: errorMessageId }),
          })
        )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    projectUuid,
    types,
    enabled,
    errorMessageId,
    fetchListAsReferencedEntity,
    dispatch,
  ])

  return { options, parentMap, isLoading }
}
